(() => {
  const MOBILE_MQ = window.matchMedia('(max-width: 767.98px)');

  function whenSwiperReady(cb) {
    if (typeof Swiper !== 'undefined') { cb(); return; }
    const tag = document.querySelector('script[src*="swiper-bundle.min.js"]');
    if (tag) { tag.addEventListener('load', cb, { once: true }); return; }
    const timer = setInterval(() => {
      if (typeof Swiper !== 'undefined') { clearInterval(timer); cb(); }
    }, 50);
    setTimeout(() => clearInterval(timer), 10000);
  }

  class ScrollStackGallery {
    constructor(mediaGallery) {
      this.mediaGallery = mediaGallery;
      this.thumbsEl = mediaGallery.querySelector('[data-scroll-stack-thumbs]');
      this.imagesEl = mediaGallery.querySelector('[data-scroll-stack-images]');
      this.mobileEl = mediaGallery.querySelector('[data-scroll-stack-mobile]');

      this.thumbs = this.thumbsEl
        ? [...this.thumbsEl.querySelectorAll('[data-thumb-media]')]
        : [];
      this.items = this.imagesEl
        ? [...this.imagesEl.querySelectorAll('[data-scroll-item]')]
        : [];

      this.observer = null;
      this.mobileSwiper = null;
      this.mqListener = () => this.handleBreakpointChange();

      mediaGallery.scrollStackInstance = this;

      this.bindThumbs();
      this.initByBreakpoint();
      MOBILE_MQ.addEventListener('change', this.mqListener);
    }

    isInQuickbuy() {
      return !!this.mediaGallery?.closest('.quickbuy-container');
    }

    initByBreakpoint() {
      if (MOBILE_MQ.matches || this.isInQuickbuy()) {
        this.stopObserver();
        this.initMobileSwiper();
      } else {
        this.destroyMobileSwiper();
        this.startObserver();
      }
    }

    handleBreakpointChange() {
      this.initByBreakpoint();
    }

    bindThumbs() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (MOBILE_MQ.matches || this.isInQuickbuy()) return;
          const mediaId = thumb.dataset.thumbMedia;
          const item = this.imagesEl?.querySelector(`[data-scroll-item="${mediaId}"]`);
          if (!item) return;

          const headerOffset = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0'
          ) + 20;

          const top = item.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });
    }

    startObserver() {
      if (!this.imagesEl || !this.items.length) return;
      this.stopObserver();

      // Cache header height once — recompute only on resize, not every scroll
      this._cachedHeaderH = this._readHeaderH();
      this._resizeHandler = () => { this._cachedHeaderH = this._readHeaderH(); };
      window.addEventListener('resize', this._resizeHandler, { passive: true });

      // Set active correctly at init
      const initial = this._getMostVisibleItem();
      if (initial) this.setActive(initial.dataset.scrollItem);

      this._rafId = null;
      this._scrollHandler = () => {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          const item = this._getMostVisibleItem();
          if (item) this.setActive(item.dataset.scrollItem);
        });
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    _readHeaderH() {
      return (parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height') || '0'
      ) || 0) + 20;
    }

    // Returns the topmost image that is still visible below the sticky header.
    // Active thumb = the image currently "at the top" of the scroll view.
    _getMostVisibleItem() {
      const headerH = this._cachedHeaderH ?? this._readHeaderH();

      for (const item of this.items) {
        if (item.getBoundingClientRect().bottom > headerH) return item;
      }

      return this.items[this.items.length - 1] || null;
    }

    stopObserver() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    setActive(mediaId) {
      const strId = String(mediaId);
      let activeThumb = null;

      this.thumbs.forEach((t) => {
        const isActive = t.dataset.thumbMedia === strId;
        t.classList.toggle('is-active', isActive);
        if (isActive) activeThumb = t;
      });

      // Scroll thumbnail list instantly (no smooth) to avoid fighting with page scroll
      if (activeThumb && this.thumbsEl) {
        const thumbTop = activeThumb.offsetTop;
        const thumbBottom = thumbTop + activeThumb.offsetHeight;
        const cTop = this.thumbsEl.scrollTop;
        const cH = this.thumbsEl.clientHeight;
        if (thumbTop < cTop) {
          this.thumbsEl.scrollTop = thumbTop;
        } else if (thumbBottom > cTop + cH) {
          this.thumbsEl.scrollTop = thumbBottom - cH;
        }
      }
    }

    goToMediaId(mediaId, speed) {
      const strId = String(mediaId);
      if (MOBILE_MQ.matches || this.isInQuickbuy()) {
        if (!this.mobileSwiper || !this.mobileEl) return;
        const slides = [...this.mobileEl.querySelectorAll('.swiper-slide[data-media-id]')];
        const idx = slides.findIndex((s) => s.dataset.mediaId === strId);
        if (idx >= 0) this.mobileSwiper.slideTo(idx, speed ?? 300);
      } else {
        const item = this.imagesEl?.querySelector(`[data-scroll-item="${strId}"]`);
        if (item) {
          const headerOffset = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0'
          ) + 20;
          const top = item.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
        this.setActive(strId);
      }
    }

    initMobileSwiper() {
      if (!this.mobileEl || this.mobileSwiper) return;

      whenSwiperReady(() => {
        if ((!MOBILE_MQ.matches && !this.isInQuickbuy()) || this.mobileSwiper) return;

        const paginationEl = this.mobileEl.querySelector('.swiper-pagination');
        const prevEl = this.mobileEl.querySelector('.slider-nav__btn[name="prev"]');
        const nextEl = this.mobileEl.querySelector('.slider-nav__btn[name="next"]');

        const config = {
          direction: 'horizontal',
          slidesPerView: 1,
          spaceBetween: 0,
          watchOverflow: true,
          observer: true,
          observeParents: true,
        };

        if (paginationEl) {
          config.pagination = { el: paginationEl, clickable: true };
        }
        if (prevEl && nextEl) {
          config.navigation = { prevEl, nextEl };
        }

        this.mobileSwiper = new Swiper(this.mobileEl, config);

        // When in quickbuy, wire swiperGalleryInstance so thumbnail clicks navigate this swiper
        if (this.isInQuickbuy() && this.mediaGallery) {
          const swiper = this.mobileSwiper;
          const mobileEl = this.mobileEl;
          this.mediaGallery.swiperGalleryInstance = {
            goToMediaId(mediaId, speed) {
              const slides = [...mobileEl.querySelectorAll('.swiper-slide[data-media-id]')];
              const idx = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
              if (idx >= 0) swiper.slideTo(idx, speed ?? 300);
            }
          };
        }
      });
    }

    destroyMobileSwiper() {
      if (this.mobileSwiper) {
        this.mobileSwiper.destroy(true, true);
        this.mobileSwiper = null;
      }
    }

    destroy() {
      MOBILE_MQ.removeEventListener('change', this.mqListener);
      this.stopObserver();
      this.destroyMobileSwiper();
      if (this.mediaGallery?.scrollStackInstance === this) {
        this.mediaGallery.scrollStackInstance = null;
      }
    }
  }

  function initQuickbuySwiper(mobileEl) {
    if (mobileEl.hasAttribute('data-scroll-qb-init')) return;
    mobileEl.setAttribute('data-scroll-qb-init', '');

    whenSwiperReady(() => {
      if (mobileEl.swiper) return;

      const paginationEl = mobileEl.querySelector('.swiper-pagination');
      const prevEl = mobileEl.querySelector('.slider-nav__btn[name="prev"]');
      const nextEl = mobileEl.querySelector('.slider-nav__btn[name="next"]');

      const config = {
        direction: 'horizontal',
        slidesPerView: 1,
        spaceBetween: 0,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      };
      if (paginationEl) config.pagination = { el: paginationEl, clickable: true };
      if (prevEl && nextEl) config.navigation = { prevEl, nextEl };

      const swiper = new Swiper(mobileEl, config);

      // Hook into media-gallery's swiperGalleryInstance so thumbnail clicks
      // call setActiveMedia() → goToMediaId() → our swiper navigates
      const mediaGallery = mobileEl.closest('media-gallery');
      if (mediaGallery) {
        mediaGallery.swiperGalleryInstance = {
          goToMediaId(mediaId, speed) {
            const slides = [...mobileEl.querySelectorAll('.swiper-slide[data-media-id]')];
            const idx = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
            if (idx >= 0) swiper.slideTo(idx, speed ?? 300);
          }
        };
      }
    });
  }

  function init() {
    // 1. Init new ScrollStackGallery instances (main PDP context)
    document.querySelectorAll('[data-scroll-stack-thumbs]:not([data-scroll-stack-init])').forEach((thumbsEl) => {
      thumbsEl.setAttribute('data-scroll-stack-init', '');
      const mediaGallery = thumbsEl.closest('media-gallery');
      if (mediaGallery) new ScrollStackGallery(mediaGallery);
    });

    // 2. Handle quickbuy context
    document.querySelectorAll('.quickbuy-container [data-scroll-stack-mobile]').forEach((mobileEl) => {
      const mediaGallery = mobileEl.closest('media-gallery');
      if (mediaGallery?.scrollStackInstance) {
        // Existing instance from main PDP — switch it to quickbuy mode
        mediaGallery.scrollStackInstance.initByBreakpoint();
      } else {
        // Fresh fetched quickbuy HTML — initialize swiper directly
        initQuickbuySwiper(mobileEl);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Re-init when quick buy drawer opens
  document.addEventListener('on:quickbuy:after-open', init);
})();
